import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from 'src/redux/store';
import { PortRequest, PortResponse, PortsClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';

export interface Port extends PortResponse { }

export interface State {
  isLoading: boolean;
  error: boolean;
  portList: Port[];
  selectedPort?: Port;
}

const initialState: State = {
  isLoading: false,
  error: false,
  portList: [],
};

const portSlice = createSlice({
  name: 'port',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
    },
    hasError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    getPortListSuccess: (state, action) => {
      state.portList = action.payload;
      state.isLoading = false;
    },
    getPortSuccess: (state, action: PayloadAction<PortResponse | undefined>) => {
      state.selectedPort = action.payload;
      state.isLoading = false;
    },
    addNewPortSuccess: (state, action: PayloadAction<PortResponse>) => {
      const newPort = action.payload;
      state.portList = [newPort, ...state.portList];
      state.isLoading = false;
    },
    updatePortSuccess: (state, action: PayloadAction<PortResponse>) => {
      const updatedItem = action.payload;
      const index = state.portList.findIndex(item => item.portId === updatedItem.portId);
      state.portList = [...state.portList.slice(0, index), updatedItem, ...state.portList.slice(index + 1)];
      state.isLoading = false;
    },
  },
});

// Reducer
export default portSlice.reducer

// Actions
export const {
  startLoading,
  hasError,
  getPortListSuccess,
  getPortSuccess,
  addNewPortSuccess,
  updatePortSuccess,
} = portSlice.actions;

// for useSelector
export const selectPort = (state: RootState) => state.port as State;

// ----------------------------------------------------------------------
export function getAllPorts(ignoreCache: boolean = false) {
  return async (dispatch, getState) => {
    try {
      const { portList } = selectPort(getState());
      if (ignoreCache || portList.length === 0) {
        dispatch(portSlice.actions.startLoading());
        const client = new PortsClient(undefined, axios);
        const response = await client.getAll(false);
        dispatch(portSlice.actions.getPortListSuccess(response));
      }
    } catch (error) {
      dispatch(portSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function getPort(id: number) {
  return async (dispatch, getState) => {
    if (id > 0) {
      dispatch(portSlice.actions.startLoading());
      try {
        const client = new PortsClient(undefined, axios);
        const response = await client.get(id);
        dispatch(portSlice.actions.getPortSuccess(response));
      } catch (error) {
        dispatch(portSlice.actions.hasError(true));
        handleError(error);
      }
    } else {
      dispatch(portSlice.actions.getPortSuccess());
    }
  };
}

export function createPort(port: Port, showNotification: boolean = true) {
  return async (dispatch, getState) => {
    dispatch(portSlice.actions.startLoading());
    try {
      const { portId, ...payload } = port;
      const request = payload as PortRequest;
      const client = new PortsClient(undefined, axios);
      const response = await client.create(request);
      dispatch(portSlice.actions.addNewPortSuccess(response));
      if (showNotification) {
        toast.success(`'${response.portName}' created successfully!`);
      }
    } catch (error) {
      dispatch(portSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function updatePort(port: Port, showNotification: boolean = true) {
  return async (dispatch, getState) => {
    dispatch(portSlice.actions.startLoading());
    try {
      const { portId, ...payload } = port;
      const request = payload as PortRequest;
      const client = new PortsClient(undefined, axios);
      const response = await client.edit(portId, request);
      dispatch(portSlice.actions.updatePortSuccess(response));
      if (showNotification) {
        toast.success(`${response.portName} ${response.isActive ? 'updated' : 'deleted'} successfully!`);
      }
    } catch (error) {
      dispatch(portSlice.actions.hasError(true));
      handleError(error);
    }
  };
}